import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../../store/AuthContext";
import { Button } from "@/components/ui/button";
import {
  LogOut,
  LayoutDashboard,
  ShoppingCart,
  WalletCards,
  ReceiptText,
  ClipboardList,
  HandCoins,
} from "lucide-react";
import { ErrorBoundary } from "../common/ErrorBoundary";
import { getRoleDisplayLabel } from "../../utils/roleDisplay";
import { useRestaurantPrintStation } from "../../hooks/useRestaurantPrintStation";

const NavigationLink = ({ to, children, icon: Icon }) => (
  <NavLink
    to={to}
    className={({ isActive }) =>
      `flex shrink-0 items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
        isActive ? "bg-gold-500 text-navy-900" : "text-white/85 hover:bg-white/10"
      }`
    }
  >
    <Icon className="h-4 w-4" />
    {children}
  </NavLink>
);

export const ResCashierLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  useRestaurantPrintStation(user?.roleName === "RES_CASHIER");

  const initials = (user?.fullName || user?.username || "C")
    .split(" ")
    .map((part) => part[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const doLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <header className="bms-sidebar shadow-sm">
        <div className="flex items-center justify-between gap-3 px-4 py-3 md:px-6">
          <div className="flex min-w-0 items-center gap-6">
            <div className="flex items-center gap-3">
              <div className="grid h-10 w-10 place-items-center rounded-xl bg-gold-500 font-display font-bold text-navy-900">THA</div>
              <div>
                <div className="font-display text-base font-bold leading-none text-white md:text-lg">Restaurant Cashier</div>
                <div className="text-[10px] uppercase tracking-widest text-white/60">POS Portal</div>
              </div>
            </div>
            <nav className="hidden items-center gap-1 md:flex">
              <NavigationLink to="/res-cashier/pos" icon={ShoppingCart}>Restaurant POS</NavigationLink>
              <NavigationLink to="/res-cashier/orders" icon={ClipboardList}>Restaurant Orders</NavigationLink>
              <NavigationLink to="/res-cashier/dashboard" icon={LayoutDashboard}>Dashboard</NavigationLink>
              <NavigationLink to="/res-cashier/cash-session" icon={WalletCards}>Cash Session</NavigationLink>
              <NavigationLink to="/res-cashier/my-sales" icon={ReceiptText}>My Sales</NavigationLink>
              <NavigationLink to="/res-cashier/cash-settlements" icon={HandCoins}>Cash &amp; Settlements</NavigationLink>
            </nav>
          </div>
          <div className="flex items-center gap-2 md:gap-3">
            <div className="hidden text-right md:block">
              <div className="text-sm font-medium text-white">{user?.fullName || user?.username}</div>
              <div className="text-xs text-white/65">{getRoleDisplayLabel(user?.roleName)}</div>
            </div>
            <div className="grid h-9 w-9 place-items-center rounded-full bg-white font-semibold text-navy-800 md:h-10 md:w-10">
              {initials}
            </div>
            <Button size="sm" className="border-0 bg-white/15 px-2 text-white hover:bg-white/25 md:px-3" onClick={doLogout} aria-label="Logout">
              <LogOut className="h-4 w-4 md:mr-2" /><span className="hidden md:inline">Logout</span>
            </Button>
          </div>
        </div>
        <nav className="flex gap-1 overflow-x-auto border-t border-white/10 px-3 py-2 md:hidden">
          <NavigationLink to="/res-cashier/pos" icon={ShoppingCart}>Restaurant POS</NavigationLink>
          <NavigationLink to="/res-cashier/orders" icon={ClipboardList}>Restaurant Orders</NavigationLink>
          <NavigationLink to="/res-cashier/dashboard" icon={LayoutDashboard}>Dashboard</NavigationLink>
          <NavigationLink to="/res-cashier/cash-session" icon={WalletCards}>Cash Session</NavigationLink>
          <NavigationLink to="/res-cashier/my-sales" icon={ReceiptText}>My Sales</NavigationLink>
          <NavigationLink to="/res-cashier/cash-settlements" icon={HandCoins}>Cash &amp; Settlements</NavigationLink>
        </nav>
      </header>
      <main className="min-w-0 flex-1 overflow-x-hidden p-4 md:p-6">
        <ErrorBoundary><Outlet /></ErrorBoundary>
      </main>
    </div>
  );
};
